/**
 * @license
 * Copyright Tyler Technologies, Inc. 
 * License: Apache-2.0
 */
import { getShadowElement, randomChars, toggleAttribute } from '@tylertech/forge-core';
import { BaseAdapter } from '../core/base/base-adapter';
import { EXPANSION_PANEL_CONSTANTS } from './expansion-panel-constants';
export class ExpansionPanelAdapter extends BaseAdapter {
    constructor(component) {
        super(component);
        this._transitionStartListener = this._onTransitionStart.bind(this);
        this._transitionEndListener = this._onTransitionEnd.bind(this);
        this._headerElement = getShadowElement(this._component, EXPANSION_PANEL_CONSTANTS.selectors.HEADER);
        this._contentElement = getShadowElement(this._component, EXPANSION_PANEL_CONSTANTS.selectors.CONTENT);
        this._innerElement = getShadowElement(this._component, EXPANSION_PANEL_CONSTANTS.selectors.INNER);
        this._defaultSlotElement = getShadowElement(this._component, EXPANSION_PANEL_CONSTANTS.selectors.DEFAULT_SLOT);
    }
    get triggerElement() {
        return this._triggerElement;
    }
    get _slottedContentElement() {
        return this._defaultSlotElement.assignedElements({ flatten: true })[0];
    }
    set _slottedContentId(id) {
        this._slottedContentElement?.setAttribute('id', id);
    }
    get _slottedContentId() {
        return this._slottedContentElement?.getAttribute('id') ?? '';
    }
    setTriggerElementById(id) {
        this._triggerElement = this._getTriggerElementById(id);
    }
    setAnimationCompleteListener(listener) {
        this._transitionCompleteListener = listener;
        this._contentElement.addEventListener('transitionstart', this._transitionStartListener);
        this._contentElement.addEventListener('transitionend', this._transitionEndListener);
    }
    addContentSlotListener(listener) {
        this._defaultSlotElement.addEventListener('slotchange', listener);
    }
    addHeaderListener(type, listener) {
        this._headerElement.addEventListener(type, listener);
    }
    removeTriggerListeners(options = { reset: false }) {
        this._triggerListenerController?.abort();
        if (options?.reset) {
            this._triggerListenerController = new AbortController();
        }
    }
    addTriggerListener(type, listener) {
        this._triggerElement?.addEventListener(type, listener, { signal: this._triggerListenerController.signal });
    }
    tryToggleOpenIcon(value) {
        const openIconElement = this._component.querySelector(EXPANSION_PANEL_CONSTANTS.selectors.OPEN_ICON);
        if (openIconElement) {
            openIconElement.open = value;
        }
    }
    setContentVisibility(visible) {
        this._contentElement.classList.toggle(EXPANSION_PANEL_CONSTANTS.classes.HIDDEN, !visible);
    }
    _onTransitionStart(evt) {
        if (evt.propertyName.startsWith('grid-template')) {
            this.toggleHostAttribute(EXPANSION_PANEL_CONSTANTS.attributes.OPENING, true);
        }
    }
    _onTransitionEnd(evt) {
        if (evt.propertyName.startsWith('grid-template')) {
            this.toggleHostAttribute(EXPANSION_PANEL_CONSTANTS.attributes.OPENING, false);
            this._transitionCompleteListener();
        }
    }
    animationStart() {
        this._innerElement.style.overflow = 'hidden';
    }
    animationEnd() {
        this._innerElement.style.removeProperty('overflow');
    }
    setTriggerElement(el) {
        this._triggerElement = el;
    }
    setContentId() {
        if (!this._slottedContentId) {
            this._slottedContentId = `forge-expansion-panel-content-${randomChars()}`;
        }
    }
    updateAriaControls() {
        if (this._triggerElement) {
            toggleAttribute(this._triggerElement, !!this._slottedContentId, 'aria-controls', this._slottedContentId);
        }
    }
    updateAriaExpanded(open) {
        if (this._triggerElement) {
            toggleAttribute(this._triggerElement, true, 'aria-expanded', open.toString());
        }
    }
    detachTriggerAria() {
        this._triggerElement?.removeAttribute('aria-controls');
        this._triggerElement?.removeAttribute('aria-expanded');
    }
    _getTriggerElementById(id) {
        if (id) {
            const rootNode = this._component.getRootNode();
            return rootNode.getElementById(id);
        }
        else {
            return null;
        }
    }
}
